import { useEffect, useMemo } from 'react';

import { useAtomValue } from 'jotai';
import { useLocation } from 'react-router-dom';

import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { selectedPlanAtom } from '../atoms';

interface BenefitsPageSidebarProps {
  activeSection?: string;
  onSectionClick?: (sectionId: string) => void;
  onTitleClick?: (sectionId: string) => void;
}

export const BenefitsPageSidebar = ({
  activeSection,
  onSectionClick,
  onTitleClick,
}: BenefitsPageSidebarProps) => {
  const location = useLocation();
  const currentPlan = useAtomValue(selectedPlanAtom);
  const { trackEvent } = useTrackEvent();

  // Generate coverage details subsections from the currently selected plan
  const coverageDetailsSubsections = useMemo(() => {
    if (!currentPlan?.coverage) {
      return [];
    }

    return currentPlan.coverage.map((coverage) => ({
      id:
        coverage.categoryName
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '') + '-details',
      title: coverage.categoryName,
    }));
  }, [currentPlan]);

  const isSectionActive = (sectionId: string) => {
    return activeSection === sectionId;
  };

  // Check if a title should be bold (when any of its subsections are active)
  const isTitleBold = (item: (typeof sidebarItems)[0]) => {
    return (
      item.subItems?.some((subItem) => isSectionActive(subItem.id)) || false
    );
  };

  // Check if a title should have selected state (when it has no subsections and is active)
  const isTitleSelected = (item: (typeof sidebarItems)[0]) => {
    return (item.subItems?.length || 0) === 0 && isSectionActive(item.id);
  };

  // Check if a title has subsections (has any subItems)
  const hasSubsections = (item: (typeof sidebarItems)[0]) => {
    return (item.subItems?.length || 0) > 0;
  };

  // Get className for title button based on its state
  const getTitleClassName = (item: (typeof sidebarItems)[0]) => {
    const baseClasses =
      'vai-text-left vai-text-sm vai-transition-colors vai-w-full vai-px-2 vai-py-1 vai-border-0 vai-cursor-pointer';

    const isStandalone = !hasSubsections(item);

    // Standalone titles
    if (isStandalone) {
      if (isTitleSelected(item)) {
        return `${baseClasses} vai-bg-gray-100 vai-text-gray-900 vai-font-semibold vai-rounded-md`;
      }
      return `${baseClasses} vai-bg-transparent vai-text-gray-900 vai-font-normal hover:vai-bg-gray-100 hover:vai-text-gray-900 hover:vai-rounded-md`;
    }

    // Titles with subsections
    if (isTitleBold(item)) {
      return `${baseClasses} vai-bg-transparent vai-text-gray-900 vai-font-semibold`;
    }
    return `${baseClasses} vai-bg-transparent vai-text-gray-900 vai-font-normal`;
  };

  const sidebarItems = useMemo(() => {
    const baseItems: Array<{
      id: string;
      title: string;
      subItems: Array<{ id: string; title: string }>;
    }> = [
      {
        id: 'plan-summary-section',
        title: 'Plan Summary',
        subItems: [],
      },
      {
        id: 'patient-information-section',
        title: 'Patient Information',
        subItems: [],
      },
      {
        id: 'coverage-summary-section',
        title: 'Coverage Summary',
        subItems: [],
      },
      {
        id: 'coverage-details',
        title: 'Coverage Details',
        subItems: coverageDetailsSubsections,
      },
    ];

    baseItems.push(
      {
        id: 'maximums-deductibles-section',
        title: 'Maximums & Deductibles',
        subItems: [],
      },
      {
        id: 'limitations-and-disclaimers-section',
        title: 'Limitations & Disclaimers',
        subItems: [],
      },
      {
        id: 'waiting-periods-section',
        title: 'Waiting Periods',
        subItems: [],
      },
      {
        id: 'treatment-history-section',
        title: 'Treatment History',
        subItems: [],
      },
    );

    return baseItems;
  }, [coverageDetailsSubsections]);

  // Handle smooth scrolling when hash changes
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [location.hash]);

  return (
    <div className="vai-w-64 vai-h-full vai-flex vai-flex-col">
      {/* Navigation Items */}
      <div className="vai-flex-1 vai-overflow-y-auto">
        {sidebarItems.map((item) => (
          <div key={item.id} className={hasSubsections(item) ? 'vai-mb-2' : ''}>
            {/* Section Title */}
            <div className="vai-px-3 vai-py-2">
              <button
                onClick={() => {
                  trackEvent(mixpanelEvents.autoVerifyBenefitsSidebarClick, { section: item.id });
                  onTitleClick?.(item.id);
                  const element = document.getElementById(item.id);
                  if (element) {
                    element.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }
                }}
                className={getTitleClassName(item)}
              >
                {item.title}
              </button>
            </div>

            {/* Sub-items */}
            {item.subItems?.map((subItem) => (
              <div key={subItem.id} className="vai-mb-1">
                <button
                  onClick={() => {
                    trackEvent(mixpanelEvents.autoVerifyBenefitsSidebarClick, { section: subItem.id });
                    onSectionClick?.(subItem.id);
                    const element = document.getElementById(subItem.id);
                    if (element) {
                      element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                    }
                  }}
                  className={`vai-block vai-text-left vai-py-1 vai-pl-6 vai-pr-2 vai-text-sm vai-transition-colors vai-ml-3 vai-mr-0 vai-w-full ${
                    isSectionActive(subItem.id)
                      ? 'vai-bg-gray-100 vai-text-gray-900 vai-font-semibold vai-rounded-md'
                      : 'vai-text-gray-600 vai-font-normal hover:vai-bg-gray-100 hover:vai-text-gray-900 hover:vai-rounded-md'
                  }`}
                >
                  {subItem.title}
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
